import { inject } from "mobx-react";
import * as React from "react";
import { RouteStore } from "bernie-core";
import { UitkLink } from "uitk-react-link";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkMark, UitkMarkSize } from "uitk-react-mark";
import { useLocalization } from "@shared-ui/localization-context";
import {
  UitkMessagingCard,
  UitkMessagingCardMedia,
  UitkMessagingCardImageLayout,
  UitkMessagingCardSection,
} from "uitk-react-messaging-cards";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";

const authUrlGenerator = (url: string) => (routeStore?: RouteStore): string => {
  const pathname = routeStore?.pathname || "";
  const queries = routeStore?.search || "";

  const currentUrl = pathname + queries;

  return url + currentUrl;
};

const MarkBrandMap = {
  expedia: "mod_exp",
  lastminute: undefined,
  orbitz: "mod_orb",
  travelocity: "mod_tvly",
  wotif: "mod_wotif",
};

interface LoyaltySignupProps {
  context?: ExtendedContextStore;
  routeStore?: RouteStore;
  classNames?: string;
}

export const LoyaltySignup: React.FC<LoyaltySignupProps> = inject(
  "context",
  "routeStore"
)((props: LoyaltySignupProps) => {
  const { classNames = "", context, routeStore } = props;
  const isAuth = context && context.user && context.user.state !== "anonymous";

  const defaultUndefinedMark = "lastminute";
  const markName = MarkBrandMap[context?.site?.brand ?? defaultUndefinedMark];

  const { formatText } = useLocalization();

  const loyaltySignupTitle = formatText("loyatlySignup.title");
  const loyaltySignupSubtitle = formatText("loyatlySignup.subtitle");
  const linkConfig = [
    {
      message: formatText("loyatlySignup.signUp"),
      trackedLinkRffr: "signUp",
      createGetUrl: authUrlGenerator("/user/createaccount?uurl=e3id=redr%26rurl%3D%2F"),
    },
    {
      message: formatText("loyatlySignup.singIn"),
      trackedLinkRffr: "signIn",
      createGetUrl: authUrlGenerator("/user/signin?uurl=e3id=redr%26rurl%3D%2F"),
    },
  ];

  if (isAuth) {
    return null;
  }

  return (
    <UitkMessagingCard className={`LoyaltySignup ${classNames}`} imageLayout={UitkMessagingCardImageLayout.LEFT}>
      {markName && (
        <UitkLayoutFlex>
          <UitkLayoutFlexItem>
            <UitkMessagingCardMedia>
              <div data-testid="loyalty-signup-mark">
                <UitkMark name={markName} size={UitkMarkSize.MEDIUM_LARGE} description={loyaltySignupTitle} />
              </div>
            </UitkMessagingCardMedia>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      )}
      {!markName && (
        <UitkMessagingCardMedia withBackground={true}>
          <div data-testid="loyalty-signup-icon">
            <UitkIcon name="mod" size={UitkIconSize.MEDIUM} className="loyaltySignUpIcon" />
          </div>
        </UitkMessagingCardMedia>
      )}
      <UitkMessagingCardSection>
        {loyaltySignupTitle && (
          <UitkHeading level={3} size={300}>
            {loyaltySignupTitle}
          </UitkHeading>
        )}
        {loyaltySignupSubtitle && <UitkText size={300}>{loyaltySignupSubtitle}</UitkText>}
        <div className="all-t-margin-two">
          {linkConfig.map(({ createGetUrl, ...linkProps }) => {
            return <AuthLink key={linkProps.trackedLinkRffr} href={createGetUrl(routeStore)} {...linkProps} />;
          })}
        </div>
      </UitkMessagingCardSection>
    </UitkMessagingCard>
  );
});

interface AuthLinkProps {
  message: string;
  trackedLinkRffr: string;
  href: string;
}

const AuthLink: React.FC<AuthLinkProps> = (props) => {
  const { trackedLinkRffr, message, href } = props;

  return (
    <UitkLink inline={true} className="all-r-margin-six">
      <TrackedLink
        href={href}
        moduleName="Hotels"
        linkName={trackedLinkRffr}
        rfrr={`loyaltySignup.actionLink.${trackedLinkRffr}`}
      >
        {message}
      </TrackedLink>
    </UitkLink>
  );
};

export default LoyaltySignup;
